import { ReactElement } from 'react';
import { TYPES_TO_NAMES } from '@blisspointmedia/bpm-types/dist/LinearBuying';
import { Button } from '@Components/Button';
import { Appearance, Size } from '@Components/designConstants';
import { NumberFormatter } from '@Components/NumberFormatter/NumberFormatter';
import { Header } from '@Components/StickyTable/BPMTable';
import { LogSummaryRow } from './use-admin-data';

export const AdminAction = {
  UPDATE_TRAFFIC: 'update-traffic',
  DOWNLOAD_TAPES: 'download-tapes',
  CLEAR_SHEETS: 'clear-sheets',
  CLEAR_COMPANIES: 'clear-companies'
} as const;
export type AdminActionType = (typeof AdminAction)[keyof typeof AdminAction];

const renderTypeCell = (row: LogSummaryRow): string => TYPES_TO_NAMES[row.type ?? ''];

const renderPlansCostCell = (row: LogSummaryRow): ReactElement => (
  <NumberFormatter value={row.plans_cost} type='$' />
);

const renderPostlogsSpendCell = (row: LogSummaryRow): ReactElement => (
  <NumberFormatter value={row.postlogs_spend} type='$' />
);

const renderBvsSpendCell = (row: LogSummaryRow): ReactElement => (
  <NumberFormatter value={row.bvs_spend} type='$' />
);

const renderPrelogSpendCell = (row: LogSummaryRow): ReactElement => (
  <NumberFormatter value={row.prelog_spend} type='$' />
);

const TYPE_HEADER: Header = {
  label: 'Type',
  name: 'type',
  sortPriority: 4,
  sortAscending: true,
  flex: 1,
  renderer: renderTypeCell
};

export const DUPLICATES_HEADERS: Header[] = [
  { label: 'Company', name: 'company', sortPriority: 0, sortAscending: true, flex: 1 },
  { label: 'Week', name: 'week', sortPriority: 1, sortAscending: true, flex: 1 },
  { label: 'Network', name: 'network', sortPriority: 2, sortAscending: true, flex: 1 },
  { label: 'Avail', name: 'avail', sortPriority: 3, sortAscending: true, flex: 1 },
  TYPE_HEADER,
  { label: 'Count', name: 'count' }
];

export const UNACCEPTED_HEADERS: Header[] = [
  { label: 'Week', name: 'week', sortPriority: 0, sortAscending: false, flex: 1 },
  { label: 'Company', name: 'company', sortPriority: 1, sortAscending: true, flex: 1 },
  { label: 'Network', name: 'network', sortPriority: 2, sortAscending: true, flex: 1 },
  { label: 'Avail', name: 'avail', sortPriority: 3, sortAscending: true, flex: 1 },
  TYPE_HEADER,
  { label: 'Unaccepted', name: 'unaccepted' }
];

export const OVERSPEND_HEADERS: Header[] = [
  { label: 'Week', name: 'week', sortPriority: 0, sortAscending: false, flex: 1 },
  { label: 'Company', name: 'company', sortPriority: 1, sortAscending: true, flex: 1 },
  { label: 'Network', name: 'network', sortPriority: 2, sortAscending: true, width: 80 },
  { label: 'Avail', name: 'avail', sortPriority: 3, sortAscending: true, width: 60 },
  TYPE_HEADER,
  { label: 'Ordered $', name: 'plans_cost', renderer: renderPlansCostCell, flex: 1 },
  { label: 'Post $', name: 'postlogs_spend', renderer: renderPostlogsSpendCell, flex: 1 }
];

export const CONFIRMED_DNRS_HEADERS: Header[] = [
  { label: 'Week', name: 'week', sortPriority: 0, sortAscending: false, flex: 1 },
  { label: 'Company', name: 'company', sortPriority: 1, sortAscending: true, flex: 1 },
  { label: 'Network', name: 'network', sortPriority: 2, sortAscending: true, width: 80 },
  { label: 'Avail', name: 'avail', sortPriority: 3, sortAscending: true, width: 60 }
];

export const PRELOGS_ONLY_HEADERS: Header[] = [
  { label: 'Week', name: 'week', sortPriority: 0, sortAscending: false, flex: 1 },
  { label: 'Company', name: 'company', sortPriority: 1, sortAscending: false, flex: 1 },
  { label: 'Network', name: 'network', sortPriority: 2, sortAscending: false, flex: 1 },
  { ...TYPE_HEADER, sortPriority: 3 }
];

// Old bpm-frontend company emails accumulated across a few domain migrations -- strip
// whichever one shows up so the reconciler column just shows a bare username.
const RECONCILER_EMAIL_DOMAINS = [
  '@blisspointmedia.com',
  '@old.blisspointmedia.com',
  '@prospectpointmedia.com',
  '@old.prospectpointmedia.com',
  '@tinuiti.com'
];

const getReconcilerLabel = (reconciler: string): string =>
  RECONCILER_EMAIL_DOMAINS.reduce((label, domain) => label.replace(domain, ''), reconciler);

interface ReconcileStatusCellProps {
  row: LogSummaryRow;
  onUpdateReconcileStatus: (row: LogSummaryRow) => Promise<void>;
}

const ReconcileStatusCell = ({
  row,
  onUpdateReconcileStatus
}: ReconcileStatusCellProps): ReactElement => {
  if (row.status === 'DNR') {
    return (
      <span className='dnr-status'>
        <span>DNR ({getReconcilerLabel(row.reconciler ?? '')})</span>
        <Button
          size={Size.LG}
          appearance={Appearance.EMPTY}
          onClick={() => onUpdateReconcileStatus({ ...row, status: '', reconciler: 'me' })}
        >
          Undo
        </Button>
      </span>
    );
  }
  return (
    <Button
      size={Size.LG}
      appearance={Appearance.EMPTY}
      onClick={() => onUpdateReconcileStatus({ ...row, status: 'DNR', reconciler: 'me' })}
    >
      Mark DNR
    </Button>
  );
};

export const getPotentiallyMissingHeaders = (
  onUpdateReconcileStatus: (row: LogSummaryRow) => Promise<void>
): Header[] => [
  { label: 'Week', name: 'week', sortPriority: 0, sortAscending: false, flex: 1 },
  { label: 'Company', name: 'company', sortPriority: 1, sortAscending: true, flex: 1 },
  { label: 'Network', name: 'network', sortPriority: 2, sortAscending: true, width: 80 },
  { label: 'Avail', name: 'avail', sortPriority: 3, sortAscending: true, width: 60 },
  TYPE_HEADER,
  { label: 'Ordered $', name: 'plans_cost', renderer: renderPlansCostCell, flex: 1 },
  { label: 'Post $', name: 'postlogs_spend', renderer: renderPostlogsSpendCell, flex: 1 },
  { label: 'BVS $', name: 'bvs_spend', renderer: renderBvsSpendCell, flex: 1 },
  { label: 'Pre $', name: 'prelog_spend', renderer: renderPrelogSpendCell, flex: 1 },
  {
    label: 'Status',
    name: 'status',
    minFlexWidth: 150,
    renderer: (row: LogSummaryRow) => (
      <ReconcileStatusCell row={row} onUpdateReconcileStatus={onUpdateReconcileStatus} />
    ),
    flex: 1
  }
];
